import React, { useEffect, useRef, useState } from 'react';
import { findCardByName } from '../../data/cardMeanings';
import { drawChartTarot } from '../../services/chartService';
import { wrapReading } from './ReadingText';

const POSITIONS = [
  ['Sun', 'What lights you up right now'],
  ['Moon', 'What your heart needs'],
  ['Rising', "How you're meeting the world"],
  ['Venus', 'Love'],
  ['Saturn', 'The lesson in front of you'],
  ['North Node', "Where you're growing"],
];

export function TarotCards({ cards }) {
  return (
    <ol className="chart-tarot-cards">
      {cards.map((card) => {
        const meaning = findCardByName(card.name);
        const reversed = card.orientation === 'Reversed';
        return (
          <li key={card.position} className="chart-tarot-card">
            <p className="chart-tarot-position">{card.position}</p>
            <p className="chart-tarot-question">{card.question}</p>
            {meaning?.image && (
              <img
                src={meaning.image}
                alt={`${card.name}${reversed ? ' reversed' : ''}`}
                className={reversed ? 'reversed' : ''}
                loading="lazy"
              />
            )}
            <p className="chart-tarot-name">
              {card.name}
              {reversed && <span> · reversed</span>}
            </p>
            <p className="birth-note">{card.placement}</p>
            {meaning && <a href={`/tarot-card-meanings/${meaning.slug}/`}>Card meaning →</a>}
          </li>
        );
      })}
    </ol>
  );
}

const ChartTarot = ({ session, language, timeUnknown, onDrawn }) => {
  const [cards, setCards] = useState(null);
  const [reading, setReading] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const positions = POSITIONS.filter(([label]) => !(timeUnknown && label === 'Rising'));

  const pull = () => {
    if (!session || busy || cards) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(true);
    setError('');
    drawChartTarot(session, {
      signal: controller.signal,
      onCards: setCards,
      onToken: (token) => setReading((current) => current + token),
      onDone: () => {
        setBusy(false);
        onDrawn?.();
      },
      onError: (message) => {
        setError(message);
        setBusy(false);
      },
    });
  };

  if (!cards) {
    return (
      <div className="chart-tarot-intro">
        <p>
          Pull one tarot card for each key point of your chart, then read every card through its placement.
          {timeUnknown ? ' Without a birth time there is no rising sign, so the spread has five cards.' : ''}
        </p>
        <ol className="chart-tarot-positions">
          {positions.map(([label, question]) => (
            <li key={label}><strong>{label}</strong> {question}</li>
          ))}
        </ol>
        <button type="button" className="birth-draw" onClick={pull} disabled={!session || busy}>
          {busy ? 'Shuffling…' : 'Pull my cards'}
        </button>
        {!session && !error && <p className="birth-note">Your cards are ready to pull once the chart reading finishes.</p>}
        {error && <p className="birth-error">{error}</p>}
      </div>
    );
  }

  return (
    <>
      <TarotCards cards={cards} />
      {reading && <div className="chart-tarot-reading">{wrapReading(reading, language)}</div>}
      {busy && !reading && <p className="birth-wait">Reading your cards…</p>}
      {error && <p className="birth-error">{error}</p>}
    </>
  );
};

export default ChartTarot;
